import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

import { TOKEN_CLASS_GROUPS } from './token-class-groups.generated';

/**
 * `cn()` makes exactly one promise: a caller's `className` always wins over a
 * component default. Keeping that promise means telling tailwind-merge about every
 * utility Strix generates, because it only knows its own built-in value vocabularies
 * and treats anything else as an unrelated arbitrary value.
 *
 * When it does not recognise a pair, it does not error — it returns *both* classes and
 * leaves the cascade to decide by source order. Components emit their own classes
 * first and `className` last, so the component usually wins, which is precisely
 * backwards. Every one of these was silent:
 *
 * 1. `text-text-*` (semantic color) vs `text-size-*` (font size). Same prefix, so by
 *    default they are one group and `cn("text-text-accent", "text-size-cta-strong-small")`
 *    drops the color.
 * 2. `font-sans`/`font-display`/`font-mono` (family) vs `font-<role>` weights such as
 *    `font-body-medium-regular`. Same prefix, so the later class wins and font-family
 *    falls through to inheritance.
 * 3. Every spacing utility built from a token — `p-gap-300`, `px-padding-horizontal-md`,
 *    `mb-gap-100` — so a caller could not override any component's padding or margin.
 * 4. Every token whose *name* is not also a Tailwind default: `rounded-med-sm`,
 *    `rounded-lg-sm`, `shadow-demo-card`, `blur-splash`, `animate-popup-in`, and the
 *    whole `leading-height-*` and `tracking-*` scales. `rounded-md` merged and
 *    `rounded-med-sm` did not, which is a hard failure mode to guess at.
 *
 * The vocabulary comes from `token-class-groups.generated.ts`, emitted by
 * `scripts/build-tokens.mjs` from the same rows that generate the `@theme` block. It
 * is generated rather than written because a hand-maintained copy was tried — for the
 * `gap` group alone — and was both incomplete on the day it landed and stale within two
 * phases. Add a token, and it is registered here in the same run.
 */

/**
 * `{ p: ['gap-300', …] }` — the shape tailwind-merge wants for one group.
 *
 * Literal value lists, not a validator function, and the difference is load-bearing:
 * tailwind-merge matches literals through a trie *before* it consults any validator.
 * Its built-in `font-family` group validates with "any non-arbitrary value", which
 * matches `font-body-medium-regular` too — so with a validator, the weight role landed
 * in the family group and `cn('font-sans', 'font-body-medium-regular')` erased the
 * family. Literals take precedence and keep the two apart. They are also a trie lookup
 * rather than a linear scan of predicates on every class.
 */
const group = (name: string, values: readonly string[]): [Record<string, string[]>] => [{ [name]: [...values] }];

/** Expand one token namespace across every utility prefix that reads it. */
const groupsFor = (spec: { utilities: readonly string[]; values: readonly string[] }) =>
  Object.fromEntries(spec.utilities.map((utility) => [utility, group(utility, spec.values)]));

/**
 * Tailwind's spacing scale feeds many groups, and tailwind-merge's own
 * `conflictingClassGroups` already knows the shorthand relationships between them —
 * that `p` beats `px`, and `px` beats `pl`. Registering the leaf groups is therefore
 * enough for `cn("px-gap-400", "p-gap-100")` to resolve correctly.
 *
 * Listed explicitly because that is the point of the bug: registering `gap` alone left
 * `p`, `px`, `py`, `m`, `space-*`, `inset-*` and the size groups all broken.
 */
const SPACING_UTILITIES = [
  'p',
  'px',
  'py',
  'pt',
  'pr',
  'pb',
  'pl',
  'm',
  'mx',
  'my',
  'mt',
  'mr',
  'mb',
  'ml',
  'gap',
  'gap-x',
  'gap-y',
  'space-x',
  'space-y',
  'inset',
  'inset-x',
  'inset-y',
  'top',
  'right',
  'bottom',
  'left',
  'w',
  'h',
  'size',
  'min-w',
  'max-w',
  'min-h',
  'max-h',
] as const;

const twMerge = extendTailwindMerge({
  extend: {
    /**
     * `display` overrides `flex-direction`, which tailwind-merge does not assume.
     *
     * Without this, `cn('flex flex-col', 'flex')` keeps **both** `flex` and `flex-col`: the two are
     * separate groups, so the caller's `flex` replaced a `flex` that was already there and the
     * direction survived untouched. Every consumer that met it read the result the same way —
     * "I passed `flex`, the component is still a column, `cn()` is broken" — and the fix at the call
     * site was to know that `flex-row` is the only utility that lands. That makes a component's
     * internal `flex-col` part of its API whether it is documented or not, which is exactly what
     * `cn()` exists to prevent.
     *
     * Declaring the conflict one-way is the whole fix: setting a display **replaces** any direction,
     * because `flex-col` means nothing under `grid` or `block` either. It does not run backwards —
     * `cn('flex flex-col', 'items-center')` still keeps `flex-col`, since alignment is a third
     * group and touches neither.
     *
     * `flex-1` and `flex-wrap` are unaffected: tailwind-merge puts the `flex` shorthand and
     * `flex-wrap` in their own groups, not in `flex-direction`.
     */
    conflictingClassGroups: {
      display: ['flex-direction'],
    },
    classGroups: {
      // `text-size-<role>` is a font size, not a color.
      'font-size': group('text-size', TOKEN_CLASS_GROUPS.fontSize.values),
      // `font-<role>` is a font weight, not a family. Family tokens are deliberately
      // absent from this list, or `font-body-small-regular` would erase `font-sans`.
      'font-weight': group('font', TOKEN_CLASS_GROUPS.fontWeightRoles.values),
      ...groupsFor({ utilities: SPACING_UTILITIES, values: TOKEN_CLASS_GROUPS.spacing.values }),
      ...groupsFor(TOKEN_CLASS_GROUPS.radius),
      ...groupsFor(TOKEN_CLASS_GROUPS.shadow),
      ...groupsFor(TOKEN_CLASS_GROUPS.blur),
      ...groupsFor(TOKEN_CLASS_GROUPS.animate),
      ...groupsFor(TOKEN_CLASS_GROUPS.leading),
      ...groupsFor(TOKEN_CLASS_GROUPS.tracking),
      ...groupsFor(TOKEN_CLASS_GROUPS.zIndex),
      /*
       * Strix's own `@utility` rules (tokens/utilities.css), hand-registered because they come
       * from no token row. `scrollbar-pill` draws Figma's 8px thumb and `scrollbar-hidden` removes
       * the bar, so a component defaulting to one and a caller passing the other must resolve to
       * the caller's — unregistered, both emit and the component's wins on source order.
       *
       * Extended into tailwind-merge's existing `scrollbar-w` group rather than declared as a new
       * one. That group already holds Tailwind's `scrollbar-thin`/`none`/`auto`, and all four of
       * these set `scrollbar-width`; a separate group would leave `cn('scrollbar-pill',
       * 'scrollbar-none')` emitting both, which is the same silent conflict one level along.
       */
      'scrollbar-w': ['pill', 'hidden'].map((v) => `scrollbar-${v}`),
    },
  },
});

/**
 * Merge class names, resolving Tailwind conflicts so a caller's `className`
 * can always override a component default.
 *
 * @example cn('px-4', 'px-8')                       // 'px-8'
 * @example cn('text-text-accent', 'text-size-h1')   // both survive
 * @example cn('rounded-med-sm', 'rounded-sm')       // 'rounded-sm'
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export type { ClassValue };
